/**
 * Brera · Página de colección (sections/collection.liquid)
 * - Filtros y orden sin recargar: arma la dirección con el formulario, pide la sección con ?section_id=,
 *   reemplaza resultados, filtros y cantidad, y actualiza la dirección (con atrás/adelante del navegador).
 * - Panel de filtros en celular (abrir/cerrar), menús desplegables de la barra (uno abierto a la vez).
 * - "Cargar más" y scroll infinito: trae la página siguiente y agrega las tarjetas.
 * - Botones de columnas: cambian los productos por fila y se recuerdan en este navegador.
 * - Barra deslizante de precio: mueve los cuadros Desde/Hasta y se aplica al soltar.
 */
(() => {
  if (window.BreraCollection) return;

  const STORE_KEY = 'brera-collection-cols';
  const readCols = () => {
    try {
      const data = JSON.parse(localStorage.getItem(STORE_KEY));
      // Solo un objeto sirve; cualquier otra cosa guardada (número, lista, texto) se ignora
      return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    } catch (e) { return {}; }
  };
  const saveCols = (data) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* sin almacenamiento */ }
  };

  const fetchSection = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString());
    if (!res.ok) throw new Error(res.status);
    return new DOMParser().parseFromString(await res.text(), 'text/html');
  };

  const initCollection = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const sectionId = root.dataset.sectionId;
    const panel = root.querySelector('[data-filters-panel]');
    const openBtn = root.querySelector('[data-filters-open]');
    const isBar = root.classList.contains('coll--bar');
    const desktop = window.matchMedia('(min-width: 990px)');
    let observer = null;

    /* ---------- Panel en celular ---------- */
    const setPanel = (open) => {
      if (!panel) return;
      panel.classList.toggle('is-open', open);
      if (openBtn) openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open && !desktop.matches ? 'hidden' : '';
    };

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-filters-open]')) setPanel(true);
      else if (e.target.closest('[data-filters-close]') && !e.target.closest('[data-coll-link]')) setPanel(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      setPanel(false);
      if (isBar) root.querySelectorAll('.coll-filter[open]').forEach((d) => d.removeAttribute('open'));
    });

    /* ---------- Barra: un menú abierto a la vez; clic afuera lo cierra ---------- */
    root.addEventListener('toggle', (e) => {
      if (!isBar || !desktop.matches || !e.target.matches('.coll-filter') || !e.target.open) return;
      root.querySelectorAll('.coll-filter[open]').forEach((d) => { if (d !== e.target) d.removeAttribute('open'); });
    }, true);
    document.addEventListener('click', (e) => {
      if (!isBar || !desktop.matches || e.target.closest('.coll-filter')) return;
      root.querySelectorAll('.coll-filter[open]').forEach((d) => d.removeAttribute('open'));
    });

    /* ---------- Actualizar resultados ---------- */
    const openGroups = () => [...root.querySelectorAll('.coll-filter[open]')].map((d) => d.dataset.filter);

    const update = async (url, push = true, scroll = false) => {
      const results = root.querySelector('[data-coll-results]');
      results.classList.add('is-loading');
      try {
        const doc = await fetchSection(url, sectionId);
        const fresh = doc.querySelector('[data-collection]');
        if (!fresh) throw new Error('sin sección');
        const keepOpen = openGroups();
        const swap = (sel) => {
          const a = root.querySelector(sel);
          const b = fresh.querySelector(sel);
          if (a && b) a.innerHTML = b.innerHTML;
        };
        swap('[data-coll-results]');
        swap('[data-filters-form]');
        swap('[data-coll-count]');
        swap('[data-coll-total]');
        swap('[data-filters-open]');
        const btn = root.querySelector('[data-filters-open]');
        if (btn && panel) btn.setAttribute('aria-expanded', panel.classList.contains('is-open') ? 'true' : 'false');
        root.querySelectorAll('.coll-filter').forEach((d) => {
          if (isBar && desktop.matches) d.toggleAttribute('open', false);
          else if (keepOpen.includes(d.dataset.filter)) d.setAttribute('open', '');
        });
        applyCols();
        setupMore();
        if (push) history.pushState({ brera: true }, '', url);
        if (scroll) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (err) {
        window.location.href = url;
        return;
      }
      results.classList.remove('is-loading');
    };

    const formUrl = () => {
      const form = root.querySelector('[data-filters-form]');
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((v, k) => { if (String(v).trim() !== '') params.append(k, v); });
      }
      const sort = root.querySelector('[data-coll-sort]');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      const q = params.toString();
      return window.location.pathname + (q ? '?' + q : '');
    };

    /* ---------- Barra deslizante de precio ----------
       Mueve los cuadros Desde/Hasta mientras se arrastra; al soltar se aplican (evento change del formulario).
       En los extremos el cuadro queda vacío, así no aparece un filtro de precio que no filtra nada. */
    const syncRange = (range, from) => {
      const min = range.querySelector('[data-range-min]');
      const max = range.querySelector('[data-range-max]');
      const top = Number(max.max) || 0;
      // Saltos redondos según el precio más alto (de a $1.000 en pesos, de a 100 o 1 en precios bajos)
      const step = top > 20000 ? 1000 : top > 2000 ? 100 : 1;
      const round = (v) => (v >= top ? top : Math.round(v / step) * step);
      let a = round(Number(min.value));
      let b = round(Number(max.value));
      min.value = a;
      max.value = b;
      if (a > b) {
        if (from === min) { a = b; min.value = a; } else { b = a; max.value = b; }
      }
      range.style.setProperty('--from', (top ? (a / top) * 100 : 0) + '%');
      range.style.setProperty('--to', (top ? (b / top) * 100 : 100) + '%');
      const panel = range.closest('.coll-filter__panel');
      const fMin = panel && panel.querySelector('[data-price-min]');
      const fMax = panel && panel.querySelector('[data-price-max]');
      if (fMin) fMin.value = a > 0 ? a : '';
      if (fMax) fMax.value = b < top ? b : '';
    };
    root.addEventListener('input', (e) => {
      const range = e.target.closest('[data-price-range]');
      if (range) { syncRange(range, e.target); return; }
      if (e.target.matches('[data-price-min], [data-price-max]')) {
        const panel = e.target.closest('.coll-filter__panel');
        const r = panel && panel.querySelector('[data-price-range]');
        if (!r) return;
        const min = r.querySelector('[data-range-min]');
        const max = r.querySelector('[data-range-max]');
        const fMin = panel.querySelector('[data-price-min]');
        const fMax = panel.querySelector('[data-price-max]');
        min.value = fMin.value === '' ? 0 : fMin.value;
        max.value = fMax.value === '' ? max.max : fMax.value;
        const top = Number(max.max) || 1;
        r.style.setProperty('--from', (Number(min.value) / top) * 100 + '%');
        r.style.setProperty('--to', (Number(max.value) / top) * 100 + '%');
      }
    });

    let priceTimer = null;
    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-price]')) {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => update(formUrl()), 500);
      } else if (e.target.closest('[data-filters-form]') || e.target.matches('[data-coll-sort]')) {
        update(formUrl());
      }
    });
    root.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-filters-form]')) return;
      e.preventDefault();
      update(formUrl());
    });
    root.addEventListener('click', (e) => {
      const link = e.target.closest('[data-coll-link]');
      if (!link) return;
      e.preventDefault();
      update(link.href, true, link.hasAttribute('data-coll-scroll'));
    });
    window.addEventListener('popstate', () => update(window.location.href, false));

    /* ---------- Cargar más / scroll infinito ---------- */
    const loadMore = async (link) => {
      if (link.classList.contains('is-loading')) return;
      link.classList.add('is-loading');
      try {
        const doc = await fetchSection(link.href, sectionId);
        const grid = root.querySelector('[data-coll-grid]');
        doc.querySelectorAll('[data-coll-grid] > li').forEach((li) => grid.appendChild(document.adoptNode(li)));
        const footer = link.closest('[data-coll-more]');
        const total = Number(footer.dataset.total) || 0;
        const shown = Math.min(grid.querySelectorAll('[data-coll-item]').length, total);
        const count = footer.querySelector('[data-load-more-count]');
        const bar = footer.querySelector('[data-load-more-bar]');
        if (count) count.textContent = count.dataset.template.replace('[x]', shown).replace('[y]', total);
        if (bar && total) bar.style.width = Math.round((shown / total) * 100) + '%';
        const next = doc.querySelector('[data-coll-next]');
        if (next) {
          link.href = next.getAttribute('href');
          link.classList.remove('is-loading');
        } else {
          link.remove();
          if (observer) observer.disconnect();
        }
      } catch (err) {
        window.location.href = link.href;
      }
    };

    const setupMore = () => {
      if (observer) observer.disconnect();
      const link = root.querySelector('[data-coll-next]');
      if (!link) return;
      link.addEventListener('click', (e) => { e.preventDefault(); loadMore(link); });
      if (root.dataset.pagination === 'infinite' && 'IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.isIntersecting) loadMore(link); });
        }, { rootMargin: '600px 0px' });
        observer.observe(link);
      }
    };

    /* ---------- Productos por fila ----------
       Lo guardado en este navegador solo se aplica si existe un botón con ese número (p. ej. si antes había 5
       y ahora el máximo es otro, o si guardó algo raro). Si no aplica, se borra y queda lo del editor. */
    const markActive = (buttons, key, value) => {
      buttons.forEach((b) => {
        const on = b.dataset[key] === String(value);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    };

    const applyCols = () => {
      const view = root.querySelector('[data-coll-view]');
      const grid = root.querySelector('[data-coll-grid]');
      if (!view || !grid) return;
      const saved = readCols();
      let changed = false;
      [['desktop', 'colsDesktop', '--cols-desktop'], ['mobile', 'colsMobile', '--cols-mobile']].forEach(([name, key, prop]) => {
        const buttons = view.querySelectorAll(`[data-cols-${name}]`);
        const value = saved[name];
        if (value == null) return;
        const valid = Array.from(buttons).some((b) => b.dataset[key] === String(value));
        if (!valid) { delete saved[name]; changed = true; return; }
        grid.style.setProperty(prop, value);
        markActive(buttons, key, value);
      });
      if (changed) saveCols(saved);
    };

    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cols-desktop], [data-cols-mobile]');
      if (!b) return;
      const saved = readCols();
      if (b.dataset.colsDesktop) saved.desktop = Number(b.dataset.colsDesktop);
      if (b.dataset.colsMobile) saved.mobile = Number(b.dataset.colsMobile);
      saveCols(saved);
      applyCols();
    });

    applyCols();
    setupMore();
  };

  const init = (scope) => scope.querySelectorAll('[data-collection]').forEach(initCollection);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.BreraCollection = { init };
})();
